import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { HiOutlineEnvelope } from 'react-icons/hi2'
import { toast } from 'react-toastify'

import {
  useGetHelpdeskEmailSettingsQuery,
  useUpdateHelpdeskEmailSettingsMutation,
} from '../../api/services/helpdeskApi'

const HelpDeskEmailSettingsToggle = () => {
  const { t } = useTranslation()
  const { data, isLoading, isFetching, isError, refetch } = useGetHelpdeskEmailSettingsQuery()
  const [updateSettings, { isLoading: isSaving }] = useUpdateHelpdeskEmailSettingsMutation()
  const [localValue, setLocalValue] = useState()

  const isInitialLoading = (isLoading || isFetching) && !data

  const handleToggle = async (checked) => {
    if (!data) return

    const previous = data.receive_ticket_emails ?? true
    setLocalValue(checked)

    try {
      await updateSettings({ receive_ticket_emails: checked }).unwrap()
    } catch {
      setLocalValue(previous)
      toast.error(t('support.email_settings.save_error'))
    }
  }

  const renderControls = () => {
    if (isInitialLoading) {
      return (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2" aria-busy="true">
          <span className="text-sm text-slate-700">{t('support.email_settings.label')}</span>
          <div className="h-5 w-10 animate-pulse rounded-full bg-slate-200" />
          <div className="h-5 w-16 animate-pulse rounded-full bg-slate-200" />
        </div>
      )
    }

    if (isError || !data) {
      return (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <span className="text-sm text-slate-700">{t('support.email_settings.label')}</span>
          <p className="text-sm text-red-600">{t('support.email_settings.load_error')}</p>
          <button
            type="button"
            className="text-sm font-medium text-[#006EAB] hover:text-[#005a8c]"
            onClick={() => refetch()}
          >
            {t('support.email_settings.retry')}
          </button>
        </div>
      )
    }

    const receiveTicketEmails = localValue ?? data.receive_ticket_emails ?? true

    return (
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="text-sm text-slate-700">{t('support.email_settings.label')}</span>
        <input
          type="checkbox"
          className="toggle toggle-accent toggle-sm"
          checked={receiveTicketEmails}
          onChange={(event) => handleToggle(event.target.checked)}
          disabled={isSaving}
          aria-label={t('support.email_settings.label')}
        />
        <span
          className={`rounded-full px-2 py-0.5 text-xs font-medium ${
            receiveTicketEmails ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'
          }`}
        >
          {receiveTicketEmails ? t('support.email_settings.enabled') : t('support.email_settings.disabled')}
        </span>
      </div>
    )
  }

  return (
    <div className="flex min-w-0 items-start gap-3">
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#006EAB] text-white shadow-sm">
        <HiOutlineEnvelope className="h-5 w-5" aria-hidden />
      </div>

      <div className="min-w-0 flex-1 pt-0.5">
        {renderControls()}
        <p className="mt-1.5 text-xs leading-relaxed text-slate-500">{t('support.email_settings.hint')}</p>
      </div>
    </div>
  )
}

export default HelpDeskEmailSettingsToggle
